import type { IconDefinition } from "@fortawesome/fontawesome-svg-core"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faCamera,
  faCircle,
  faCloudSun,
  faDroplet,
  faMicroscope,
  faPlaneUp,
  faQuestion,
  faRobot,
  faVial,
  faWatch,
} from "@fortawesome/sharp-solid-svg-icons"
import { Box, Divider, Typography, styled } from "@mui/material"
import {
  OperationalStatus,
  SensorType,
} from "@phc/common/gen/threatAnalyticsGen"
import { extraColors } from "../../utils/theme"
import { operationalStatusChipColors, sensorTypeLabels } from "../Cop/copUtils"
import { Tag } from "../Shared/GenericTag"
import type { SensorTypeSummary } from "./useSensorSummary"
import { useSensorSummary } from "./useSensorSummary"

const sensorTypeIcons: Record<SensorType, IconDefinition> = {
  [SensorType.SENSOR_TYPE_UNSPECIFIED]: faQuestion,
  [SensorType.SENSOR_TYPE_WEATHER_STATION]: faCloudSun,
  [SensorType.SENSOR_TYPE_CAMERA]: faCamera,
  [SensorType.SENSOR_TYPE_AIR_COLLECTION]: faPlaneUp,
  [SensorType.SENSOR_TYPE_WATER_COLLECTION]: faDroplet,
  [SensorType.SENSOR_TYPE_SURFACE_COLLECTION]: faVial,
  [SensorType.SENSOR_TYPE_LABORATORY]: faMicroscope,
  [SensorType.SENSOR_TYPE_WEARABLE]: faWatch,
  [SensorType.SENSOR_TYPE_UNMANNED_SYSTEMS]: faRobot,
}

const Bar = styled(Box)({
  position: "absolute",
  bottom: 0,
  left: 0,
  right: 0,
  display: "flex",
  alignItems: "stretch",
  backgroundColor: extraColors.black10,
  backdropFilter: "blur(8px)",
  borderTop: `1px solid ${extraColors.translucentWhite}`,
  overflowX: "auto",
  overflowY: "hidden",
  zIndex: 5,
  /* thin scrollbar */
  "&::-webkit-scrollbar": { height: 4 },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: extraColors.disabled,
    borderRadius: 2,
  },
})

const Column = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 4,
  padding: "8px 16px",
  flexShrink: 0,
})

const Header = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: 6,
  whiteSpace: "nowrap",
})

const BadgeRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 4,
})

function StatusBadge({
  status,
  value,
}: {
  status?: OperationalStatus
  value: React.ReactNode
}) {
  const c = status ? operationalStatusChipColors[status] : undefined
  return (
    <Tag
      style={{
        justifyContent: "center",
        lineHeight: 1.5,
        padding: "1px 4px",
        ...(c && { backgroundColor: c.bg, color: c.fg, borderColor: c.border }),
      }}
    >
      <Typography variant="small1Bold" color="inherit">
        {value}
      </Typography>
    </Tag>
  )
}

function pct(n: number, total: number): string {
  if (total === 0) return "0%"
  return `${Math.round((n / total) * 100)}%`
}

export const SensorSummaryPanel = () => {
  const summary = useSensorSummary()

  return (
    <Bar>
      {/* ---- Total sensors ---- */}
      <Column>
        <Header>
          <FontAwesomeIcon
            icon={faCircle}
            style={{ color: extraColors.status.navyLight, fontSize: 8 }}
          />
          <Typography
            variant="caption"
            sx={{ color: extraColors.white, fontWeight: 600 }}
          >
            Sensors
          </Typography>
        </Header>
        <BadgeRow>
          <StatusBadge value={summary.total} />
        </BadgeRow>
      </Column>

      {/* ---- Overall status ---- */}
      <Column>
        <Header>
          <FontAwesomeIcon
            icon={faCircle}
            style={{ color: extraColors.status.greenLight, fontSize: 8 }}
          />
          <Typography
            variant="caption"
            sx={{ color: extraColors.white, fontWeight: 600 }}
          >
            Sensor Status
          </Typography>
        </Header>
        <BadgeRow>
          <StatusBadge
            status={OperationalStatus.OPERATIONAL_STATUS_FMC}
            value={pct(summary.status.fmc, summary.total)}
          />
          <StatusBadge
            status={OperationalStatus.OPERATIONAL_STATUS_PMC}
            value={pct(summary.status.pmc, summary.total)}
          />
          <StatusBadge
            status={OperationalStatus.OPERATIONAL_STATUS_NMC}
            value={pct(summary.status.nmc, summary.total)}
          />
          <StatusBadge
            status={OperationalStatus.OPERATIONAL_STATUS_UNSPECIFIED}
            value={pct(summary.status.unspecified, summary.total)}
          />
        </BadgeRow>
      </Column>

      {/* ---- Divider ---- */}
      <Divider
        orientation="vertical"
        flexItem
        sx={{ borderColor: extraColors.translucentWhite, my: 1 }}
      />

      {/* ---- Per sensor-type columns ---- */}
      {Object.values(SensorType).map((st, i) => {
        const data: SensorTypeSummary = summary.byType.get(st) ?? {
          total: 0,
          fmc: 0,
          pmc: 0,
          nmc: 0,
          unspecified: 0,
        }
        return (
          <Box key={st} sx={{ display: "flex", alignItems: "stretch" }}>
            {i > 0 && (
              <Divider
                orientation="vertical"
                flexItem
                sx={{ borderColor: extraColors.translucentWhite, my: 1 }}
              />
            )}
            <Column>
              <Header>
                <FontAwesomeIcon
                  icon={sensorTypeIcons[st]}
                  style={{ color: extraColors.hint, fontSize: 11 }}
                />
                <Typography variant="caption" sx={{ color: extraColors.white }}>
                  {sensorTypeLabels[st]}
                </Typography>
              </Header>
              <BadgeRow>
                <StatusBadge
                  status={OperationalStatus.OPERATIONAL_STATUS_FMC}
                  value={data.fmc}
                />
                <StatusBadge
                  status={OperationalStatus.OPERATIONAL_STATUS_PMC}
                  value={data.pmc}
                />
                <StatusBadge
                  status={OperationalStatus.OPERATIONAL_STATUS_NMC}
                  value={data.nmc}
                />
                <StatusBadge
                  status={OperationalStatus.OPERATIONAL_STATUS_UNSPECIFIED}
                  value={data.unspecified}
                />
              </BadgeRow>
            </Column>
          </Box>
        )
      })}
    </Bar>
  )
}
